import * as React from 'react';
import { Alert, MenuItem, TextField, Typography } from '@mui/material';
import { CaaAmazonRecordsTable, DnsRecordsTable } from './DnsRecordsTable';
import { PrimaryButton } from '@client/new/components/button';
import { Column, Grid } from './helpers';
import styled from 'styled-components';
import { useEffect } from 'react';
import { ResetDomainModal } from './ResetDomainModal';
import { EducationalVideoModal } from './EducationalVideoModal';
import { ConfirmDomainErrorModal } from './ConfirmDomainErrorModal';
import {
  faEnvelope,
  faArrowRotateRight,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { registerOptions } from './config';
import { Control, Controller } from 'react-hook-form';
import { Typography as TypographyDS } from '@design-system/components/typography/typography';
import { DomainFormType } from './domainForm.schema';

export function DnsConfigInstructions({
  dnsRecords,
  onSubmit,
  isLoading,
  domain,
  control,
  hostingProvider,
  onRemoveDomain,
  removeDomainLoading,
  hideConfirmDomainModal,
  showConfirmDomainModal,
  errorMessage,
  showCaaTable,
  configError,
}: {
  domain: string;
  isLoading: boolean;
  dnsRecords: string[];
  onSubmit: () => void;
  control: Control<DomainFormType, any, DomainFormType>;
  hostingProvider: string;
  onRemoveDomain: () => void;
  removeDomainLoading: boolean;
  hideConfirmDomainModal: () => void;
  showConfirmDomainModal: boolean;
  errorMessage: string;
  showCaaTable: boolean;
  configError?: string;
}) {
  const [Instructions, setInstructions] = React.useState(() => EmptyComponent);
  const [showResetModal, setShowResetModal] = React.useState(false);
  const [showVideoModal, setShowVideoModal] = React.useState(false);

  const openVideoModal = () => {
    setShowVideoModal(true);
    hideConfirmDomainModal();
  };
  const openResetModal = () => {
    setShowResetModal(true);
    hideConfirmDomainModal();
  };

  useEffect(() => {
    if (hostingProvider) {
      import(`./docs/${hostingProvider}.md`).then((module) => {
        setInstructions(() => module.ReactComponent);
      });
    }
  }, [hostingProvider]);

  return (
    <div className="flex flex-col gap-12">
      <section className="flex flex-col gap-6">
        <Typography variant="h4">
          Add the following CNAME{showCaaTable ? ' AND CAA' : ''} DNS records to
          your DNS configuration:
        </Typography>
        <div className="flex flex-col gap-4">
          <Alert severity="info" className=" bg-blue-50">
            <span className="font-bold underline">Note:</span> Inputting your
            domain name at the end in &quot;Host Name&quot; might not be
            necessary depending on your DNS provider.
          </Alert>
          <DnsRecordsTable
            rows={adjustDnsRecords({ hostingProvider, domain })(dnsRecords)}
          />
          {showCaaTable && <CaaAmazonRecordsTable />}
        </div>
      </section>

      <Grid>
        <Column>
          <div className="border-solid rounded-2xl bg-[#F0F6FE] p-6">
            <Typography variant="h4" className="pb-4">
              Instructions
            </Typography>
            <Controller
              name="hostingProvider"
              control={control}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <HostingProviderSelect
                  value={value}
                  onChange={onChange}
                  error={error?.message}
                />
              )}
            />

            <Typography variant="body2">
              <div
                className="whitespace-pre-wrap mt-4 leading-8"
                style={{ display: hostingProvider ? 'block' : 'none' }}
              >
                <MarkdownWrapper>
                  <Instructions />
                </MarkdownWrapper>
              </div>
            </Typography>
          </div>
        </Column>
        <Column>
          <Typography variant="h4">
            Confirm that your DNS records have been properly configured:
          </Typography>
          <Typography variant="body2">
            Once you have added the DNS records above to your web hosting
            provider, confirm that your domain is properly setup.
          </Typography>
          <PrimaryButton type="submit" onClick={onSubmit} isLoading={isLoading}>
            Confirm domain setup
          </PrimaryButton>
          {configError && (
            <TypographyDS
              variant="body"
              size="medium"
              color="ERROR-100"
              className="py-1"
            >
              {configError}
            </TypographyDS>
          )}
          <div className="flex flex-col gap-4">
            <p className="font-bold text-lg">Having issues?</p>
            <a
              href="mailto:creatorsupport@playbookapp.io"
              className="text-black"
            >
              <p className="flex gap-2 items-center">
                <FontAwesomeIcon icon={faEnvelope} />
                Contact support team
              </p>
            </a>
            <div onClick={openResetModal} className="px-0 w-max cursor-pointer">
              <div className="flex gap-2 items-center  text-[#DA0000]">
                <FontAwesomeIcon icon={faArrowRotateRight} />
                <span>Reset your domain and start over</span>
              </div>
            </div>
          </div>
          <ResetDomainModal
            isOpen={showResetModal}
            onClose={() => setShowResetModal(false)}
            onConfirm={onRemoveDomain}
            isSubmitting={removeDomainLoading}
          />
          <EducationalVideoModal
            isOpen={showVideoModal}
            onClose={() => setShowVideoModal(false)}
          />
          <ConfirmDomainErrorModal
            error={errorMessage}
            isOpen={showConfirmDomainModal}
            onClose={hideConfirmDomainModal}
            domain={domain}
            openVideoModal={openVideoModal}
            openResetModal={openResetModal}
          />
        </Column>
      </Grid>
    </div>
  );
}

export function HostingProviderSelect({
  value,
  onChange,
  error,
}: {
  value: string;
  onChange: (value: string) => void;
  error?: string;
}) {
  return (
    <div>
      <span className="p-1 leading-5 text-sm font-medium">
        <span>Your web hosting provider</span>{' '}
        <span className="text-[#DA0000]">*</span>
      </span>
      <TextField
        value={value}
        onChange={(e) => onChange(e.target.value)}
        select={true}
        className="w-full"
        error={!!error}
      >
        {registerOptions.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </TextField>
      {error && (
        <TypographyDS
          variant="body"
          size="small"
          color="ERROR-100"
          className="py-1 px-4"
        >
          {error}
        </TypographyDS>
      )}
    </div>
  );
}

const adjustDnsRecords =
  (settings: { domain: string; hostingProvider: string }) =>
  (dnsRecords: string[]) => {
    const adjustedRecords = dnsRecords.map((record) => {
      // eslint-disable-next-line prefer-const
      let [host, type, data] = record.trim().split(' ');

      host = host.split('.' + settings.domain)[0];

      return {
        host,
        type,
        data,
      };
    });
    return adjustedRecords;
  };

const MarkdownWrapper = styled.div`
  ol {
    list-style: decimal;
    position: relative;
    left: 1rem;
  }
`;

const EmptyComponent = () => null;
